import {
    alpha,
    Box,
    Button,
    Collapse,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Plus as AddIcon,
    X as CloseIcon,
    History as HistoryIcon,
    RefreshCcw,
    Search as SearchIcon,
} from 'lucide-react';
import React, { useState } from 'react';

import type { ChatThread } from './types';

interface ChatHistoryPanelProps {
    chatThreads: ChatThread[];
    historyExpanded: boolean;
    onToggleHistory: () => void;
    onNewChat?: () => void;
    onLoadChat?: (chatId: string) => void;
    onRefreshChats?: () => void;
    currentChatId?: string;
    langStrings?: any;
    onClose?: () => void;
}

export const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
    chatThreads,
    historyExpanded,
    onToggleHistory,
    onNewChat,
    onLoadChat,
    onRefreshChats,
    currentChatId,
    langStrings,
    onClose,
}) => {
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');

    const handleChatHistoryClick = (chatId: string) => {
        if (onLoadChat) {
            onLoadChat(chatId);
        }
    };

    return (
        <Box sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
            {/* Chat History Section with integrated search */}
            <Paper sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.7), mx: 2, mb: 1, mt: 0 }}>
                <ListItemButton sx={{ borderRadius: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 36 }}>
                        <HistoryIcon size={16} color={theme.palette.primary.main} />
                    </Box>
                    <ListItemText
                        primary={langStrings?.chatHistory || 'Chatverlauf'}
                        primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                        }}
                    />
                    {onRefreshChats && historyExpanded && (
                        <IconButton
                            size="small"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onRefreshChats();
                            }}
                            sx={{ mr: 1 }}
                        >
                            <RefreshCcw size={16} color={theme.palette.primary.main} />
                        </IconButton>
                    )}
                    {onClose && (
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon size={16} color={theme.palette.primary.main} />
                        </IconButton>
                    )}
                </ListItemButton>
                <Collapse in={historyExpanded}>
                    <Box sx={{ p: 2, pt: 1 }}>
                        {onNewChat && (
                            <Button
                                fullWidth
                                startIcon={<AddIcon size={16} />}
                                onClick={onNewChat}
                                sx={{
                                    justifyContent: 'flex-start',
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                                    mb: 2,
                                    borderRadius: 2,
                                }}
                            >
                                {langStrings?.newChat || 'Neuer Chat'}
                            </Button>
                        )}

                        {/* Search field integrated into chat history */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={langStrings?.searchPlaceholder || 'Chats durchsuchen...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon size={18} color={theme.palette.primary.main} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Typography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary, mb: 1, display: 'block' }}
                        >
                            {langStrings?.lastChats || 'Letzte Chats'}
                        </Typography>

                        <List sx={{ p: 0 }}>
                            {chatThreads.slice(0, 4).map((thread, index) => (
                                <ListItem key={thread.id} disablePadding sx={{ mb: 0.5 }}>
                                    <ListItemButton
                                        onClick={() => handleChatHistoryClick(thread.id)}
                                        sx={{
                                            borderRadius: 1,
                                            backgroundColor:
                                                currentChatId === thread.id
                                                    ? alpha(theme.palette.primary.main, 0.2)
                                                    : 'transparent',
                                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                                            py: 1,
                                        }}
                                    >
                                        <ListItemText
                                            primary={thread.title}
                                            secondary={thread.timestamp}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: {
                                                    fontSize: '0.75rem',
                                                    fontWeight: currentChatId === thread.id ? 600 : 400,
                                                    color:
                                                        currentChatId === thread.id
                                                            ? theme.palette.primary.main
                                                            : theme.palette.text.primary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                },
                                            }}
                                            secondaryTypographyProps={{
                                                variant: 'caption',
                                                sx: {
                                                    fontSize: '0.65rem',
                                                    color: theme.palette.text.secondary,
                                                },
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Collapse>
            </Paper>
        </Box>
    );
};

export default ChatHistoryPanel;
